/* eslint-disable react-hooks/rules-of-hooks */
import { Button, ExternalLink, Heading, Layout, MainLoader, Modal, ModalContent, ModalFooter, ModalHeader, Notification, removeMainLoader, SlidingMenu, Text } from "@netapp/bxp-design-system-react";
import classNames from "classnames";
import _ from "lodash";
import AppNotifications from "modules/app/components/AppNotifications";
import CategoriesNav from "modules/app/components/headerMenus/leftNav/CategoriesNav";
import SelectedServiceNav from "modules/app/components/headerMenus/leftNav/components/SelectedServiceNav";
import { MonitorCurrentConnector } from "modules/current-connector";
import React, { memo, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Redirect, Route, Switch } from 'react-router-dom';
import { isAllowed } from "utils/permissions";
import useCurrentService from "utils/useCurrentService";
import styles from "./app.module.scss";
import { appComponents as i18nComponentsNamespace } from "./i18n/namespace";
import { ReactComponent as NoLicenseIcon } from './images/aide-nolicense.svg';
import { ReactComponent as NoServicesIcon } from './images/aide-noservices.svg';
import ServiceIntegration from "./modules/app/ServiceIntegration";
import AppBootPerformanceLogger from "./modules/app/components/AppBootPerformanceLogger";
import DefaultRoute from "./modules/app/components/DefaultRoute";
import InactiveConnectorDialog from "./modules/app/components/InactiveConnectorDialog";
import MainErrorBoundary from "./modules/app/components/MainErrorBoundary";
import MainHeader from "./modules/app/components/MainHeader";
import { Forbidden } from "./modules/app/components/NoAccessComponents";
import SlidingManagementWidget from './modules/app/components/SlidingManagementWidget';

const SelectedServiceNavWrapper = memo(() => {
    const isShowingCategoriesNav = useSelector(state => state.menu.activeWidget === 'leftNav');
    return <SelectedServiceNav isShowingCategoriesNav={isShowingCategoriesNav} />;
});


const SlidingMenuWrapper = memo(({ lastNavRef, mainHeaderRef, onCategoryMenuItemClicked }) => {
    const dispatch = useDispatch();
    const isShowingCategoriesNav = useSelector(state => state.menu.activeWidget === 'leftNav');

    // Somehow stopping the propagation at bubbling phase doesn't work, so we use capture phase.
    // Add global event interceptor to prevent SlidingMenu from closing when clicking in flyout menu.
    useEffect(() => {
        if (!isShowingCategoriesNav) {
            return;
        }

        const handleFlyoutMenuClick = (e) => {
            const isInFlyoutMenu = e.target.closest('[data-flyout-menu]');

            if (isInFlyoutMenu) {
                // Check if the click is on an interactive element (buttons, links, service cards)
                const isOnInteractiveElement = e.target.closest('[data-service-card], [data-favorite-button]');

                if (!isOnInteractiveElement) {
                    // Only prevent if clicking on empty space, not on interactive elements
                    e.stopPropagation();
                    e.stopImmediatePropagation();
                    e.preventDefault();
                    return false;
                }
            }
        };

        // Use capture phase to intercept before SlidingMenu's event handlers
        document.addEventListener('click', handleFlyoutMenuClick, true);
        document.addEventListener('mousedown', handleFlyoutMenuClick, true);

        return () => {
            document.removeEventListener('click', handleFlyoutMenuClick, true);
            document.removeEventListener('mousedown', handleFlyoutMenuClick, true);
        };
    }, [isShowingCategoriesNav]);

    const handleCloseTrigger = useCallback(() => {
        dispatch({
            type: "MENU:CLOSE-WIDGET"
        });
    }, [dispatch]);

    return (
        <SlidingMenu
            isActive={isShowingCategoriesNav}
            isSlidingFromLeft={true}
            top={72}
            closeTrigger={handleCloseTrigger}
            className={styles['sliding-menu-left-nav']}
        >
            <CategoriesNav ref={lastNavRef} mainHeaderRef={mainHeaderRef} onCategoryMenuItemClicked={onCategoryMenuItemClicked} isShowingCategoriesNav={isShowingCategoriesNav} />
        </SlidingMenu>
    );
});

const TimeoutModal = () => {
    const { t } = useTranslation(i18nComponentsNamespace);
    const dispatch = useDispatch();
    const renew = () => {
        dispatch({
            type: "AUTH:RENEW-SSO"
        })
    };

    return <Modal closeTrigger={renew}>
        <ModalHeader>
            {t("authentication_renew")}
        </ModalHeader>
        <ModalContent>
            {t("session_expired")}
        </ModalContent>
        <ModalFooter>
            <Button onClick={renew}>{t("refresh")}</Button>
        </ModalFooter>
    </Modal>;
};
export const NoAccessMessage = ({headingKey, subtextKey, learnMoreLink}) => {
    const { t } = useTranslation(i18nComponentsNamespace);
    return <>
        <Layout.Container>
            <Layout.Grid centerContent>
                <Layout.GridItem lg={12} style={{ flex: "0 0 350px" }}>
                    {!learnMoreLink && <NoLicenseIcon color='text' style={{ marginTop: 120, width: 350 }} />}
                    {learnMoreLink && <NoServicesIcon color='text' style={{ marginTop: 100, width: 350 }} />}
                </Layout.GridItem>
                <Layout.GridItem lg={12}>
                    <Heading level={20} style={{ marginTop: 16, textAlign: "center" }} bold>{t(headingKey)}</Heading>
                    <Text style={{ marginTop: 16, textAlign: 'center' }}>{t(subtextKey)}</Text>
                    {learnMoreLink && <Text style={{ marginTop: 16, textAlign: 'center' }}><ExternalLink variant="text" trackLabel="no-feature-flags" href={learnMoreLink} includeArrow={true} dataTestId="no-features-error-link">
                        {t("learn_more")}
                    </ExternalLink></Text>}
                </Layout.GridItem>
            </Layout.Grid>
        </Layout.Container>
    </>
};
const AIDPShell = () => {
    const lastNavRef = useRef(null);
    const mainHeaderRef = useRef(null);

    const { t } = useTranslation(i18nComponentsNamespace);
    const dispatch = useDispatch();

    const isLoadingServices = useSelector(state => state.app.isLoadingServices);
    const isGuardrailsEnabled = useSelector(state => state.features.active['NetApp.DataMap/*'] ?? false);
    const isCuratorEnabled = useSelector(state => state.features.active['NetApp.AIDataExplorer/*'] ?? false);
    const serviceIntegrationPaths = useSelector(state => state.app.serviceIntegrationPaths);
    const { isAuthenticated, renewTimeout, status } = useSelector(state => state.auth);
    const { forbidden, isChangedAgent, isTransitionFromUnselectedToSelected, permissions } = useSelector(state => state.tenancy);

    const isFeaturesInitialized = useSelector(state => state.features.isInitialized);
    const selectedOrgRules = useSelector(state => state.tenancy.selectedOrg?.rules || []);

    const currentService = useCurrentService();
    const [containsGuardRails, setContainsGuardRails] = useState(undefined);
    const [containsCurator, setContainsCurator] = useState(undefined);

    useEffect(() => {
        if (!isLoadingServices && isAuthenticated) { //checks isAuthenticated cause feature flag manual toggles get saved with the userId
            dispatch({
                type: "APP:INITIALIZE-FEATURES"
            })
        }
    }, [isLoadingServices, dispatch, isAuthenticated]);

    useEffect(() => {
        if (isFeaturesInitialized) {
            dispatch({
                type: "MENU:INITIALIZE"
            })
            dispatch({
                type: "TENANCYV4:AIDE-PERMISSIONS"
            })
        }
    }, [isFeaturesInitialized]);

    useEffect(() => {
        if (!_.isEmpty(selectedOrgRules)) {
            setContainsCurator(isCuratorEnabled && isAllowed(selectedOrgRules, "aidp_data_explorer", "access"));
            setContainsGuardRails(isGuardrailsEnabled && isAllowed(selectedOrgRules, "aidp_data_guardrails", "access"));
        }
    }, [selectedOrgRules, isGuardrailsEnabled, isCuratorEnabled]);

    function onCategoryMenuItemClicked() {
        dispatch({
            type: "MENU:CLOSE-WIDGET"
        });
    }

    const showError = isLoadingServices || !isAuthenticated;
    const showTabs = !_.isUndefined(containsGuardRails) && (isGuardrailsEnabled || isCuratorEnabled);
    const showLoader = _.isEmpty(permissions) && !forbidden;
    const showLeftNav = true;
    const showServicesError = !showLoader && !isGuardrailsEnabled && !isCuratorEnabled

    return <div className={classNames((currentService?.isShowingSelectedServiceNav) ? styles.appBase : styles.noNavBase, { [styles['initializing']]: !showLeftNav })}>
        {process.env.REACT_APP_ENDPOINTS !== "prod" && <AppBootPerformanceLogger />}
        <MainHeader ref={mainHeaderRef} navRef={lastNavRef} noOrganizationForUser={status === 'NO-LICENSE' || showServicesError} />
        {showLeftNav && <>
            {currentService?.isShowingSelectedServiceNav && <SelectedServiceNavWrapper />}
        </>}
        <SlidingMenuWrapper lastNavRef={lastNavRef} mainHeaderRef={mainHeaderRef} onCategoryMenuItemClicked={onCategoryMenuItemClicked} />
        <div className={styles.doodads}>
            {isChangedAgent && <InactiveConnectorDialog />}
            {renewTimeout && <TimeoutModal />}
            <SlidingManagementWidget />
            <MonitorCurrentConnector />
            {isTransitionFromUnselectedToSelected && <Notification type="info" style={{ width: 820, position: "fixed", bottom: 63, left: "50%", transform: "translateX(-50%)" }}>
                {t("active_connector")}
            </Notification>}
        </div>

        <div className={styles.container}>
            {forbidden && <Forbidden />}
            <MainErrorBoundary>
                <Suspense fallback={<MainLoader />}>
                    <Switch>
                        <Route>
                            <>
                                {!showError && showLoader && <MainLoader />}
                                {showServicesError && <NoAccessMessage headingKey="aide_no_features_enabled" subtextKey="aide_tech_preview_disabled" learnMoreLink={"https://docs.netapp.com/us-en/ai-data-engine/"}/>}
                                {showError && <>
                                    {status === 'NO-LICENSE' && <NoAccessMessage headingKey="aide_no_license" subtextKey="aide_contact_admin" />}
                                    {status === 'EXPIRED-LICENSE' && <NoAccessMessage headingKey="aide_license_expired" subtextKey="aide_renew_license" />}
                                    {status === 'PENDING' && <MainLoader />}
                                </>}
                                {showTabs && <Switch>
                                    <Route path={serviceIntegrationPaths} render={() => <ServiceIntegration />} />
                                    {containsGuardRails && <Redirect to={{ pathname: "/console/data-guardrails", hash: window.location.hash }} />}
                                    {!containsGuardRails && containsCurator && <Redirect to={{ pathname: "/console/data-explorer", hash: window.location.hash }} />}
                                    <Route path="/:path" render={() => <DefaultRoute />} />
                                </Switch>}
                            </>
                        </Route>
                    </Switch>
                </Suspense>
            </MainErrorBoundary>
        </div>
    </div>
};

const AIDPApp = React.memo(() => {
    const appState = useSelector(state => state.app.appState);
    removeMainLoader();
    return (
        <>
            {appState === 'loading' && <MainLoader />}
            <AppNotifications />
            {appState === 'ready' && <AIDPShell />}
        </>
    );
});

export default AIDPApp;